import {
  readWorkspaceLocal,
  readWorkspaceLocalJson,
  WS_KEY,
} from "../../../utils/workspaceStorage";
import {
  EMPTY_SEARCH_FILTER_STATE,
  canEnableAllPeriod,
  filterStateToAdvancedParams,
  normalizeSearchFilterState,
} from "../../../utils/searchFilterState";

/** Foco padrão ao abrir o chat ("Meu dia"). */
export const DEFAULT_CHAT_FILTERS = [
  "Minhas Solicitações",
  "Pendente",
  "Em Atendimento",
];

/**
 * Chips resolvidos só no cliente.
 * Pipeline altera tipo_scope; Não Lidas vai na API com o mapa de visto local.
 */
export const CHAT_LIST_CLIENT_ONLY_FILTERS = ["Pipeline de Vendas"];

export function stripClientOnlyChatFilters(filters) {
  return (Array.isArray(filters) ? filters : []).filter(
    (f) => !CHAT_LIST_CLIENT_ONLY_FILTERS.includes(f)
  );
}

export function loadChatListFilters() {
  try {
    const parsed = readWorkspaceLocalJson(WS_KEY.CHAT_FILTERS, null);
    if (!Array.isArray(parsed)) return DEFAULT_CHAT_FILTERS;
    const valid = parsed.filter((f) => typeof f === "string");
    return valid.length ? valid : DEFAULT_CHAT_FILTERS;
  } catch {
    return DEFAULT_CHAT_FILTERS;
  }
}

export function loadChatListSearch() {
  try {
    const s = readWorkspaceLocal(WS_KEY.CHAT_SEARCH);
    return typeof s === "string" ? s : "";
  } catch {
    return "";
  }
}

/**
 * Termo enviado à API na lista do chat.
 * Texto livre (nome, trecho da última mensagem) filtra só no cliente sobre a lista carregada.
 * Protocolo numérico (#123 ou 123) ainda consulta o servidor para achar fora da página atual.
 */
export function resolveChatListApiSearchTerm(searchTerm) {
  const term = String(searchTerm || "").trim();
  if (!term) return "";
  const idPart = term.startsWith("#") ? term.slice(1).trim() : term;
  return /^\d+$/.test(idPart) ? idPart : "";
}

export function loadChatAdvancedFilterState() {
  try {
    const parsed = readWorkspaceLocalJson(WS_KEY.CHAT_ADVANCED_FILTERS, null);
    if (!parsed || typeof parsed !== "object") return EMPTY_SEARCH_FILTER_STATE;
    return normalizeSearchFilterState(parsed);
  } catch {
    return EMPTY_SEARCH_FILTER_STATE;
  }
}

/** Parâmetros avançados da API — mesma regra do hook principal do chat. */
export function resolveChatListAdvancedParams(debouncedFilters, debouncedAdvancedFilterState) {
  const filters = Array.isArray(debouncedFilters) ? debouncedFilters : [];
  const isSalesPipelineSelected = filters.includes("Pipeline de Vendas");
  const debouncedFiltersForApi = stripClientOnlyChatFilters(filters);

  const quemFilters = [
    "Minhas Solicitações",
    "Outros Usuários",
    "Fila",
    "ChatBots",
    "Agentes de IA",
  ];
  const selectedQuem = debouncedFiltersForApi.filter((f) => quemFilters.includes(f));
  const multipleQuem = selectedQuem.length > 1;

  const tipoFilters = ["ChatBots", "Agentes de IA", "Fila"];
  const selectedTipo = debouncedFiltersForApi.filter((f) => tipoFilters.includes(f));
  const singleTipo = selectedTipo.length === 1 ? selectedTipo[0] : null;
  const tipoMap = {
    ChatBots: "chatbots",
    "Agentes de IA": "agentes",
    Fila: "fila",
  };

  const tipoResponsavel =
    !multipleQuem && singleTipo && tipoMap[singleTipo]
      ? tipoMap[singleTipo]
      : debouncedAdvancedFilterState?.tipo_responsavel || null;

  const allPeriodActive =
    Boolean(debouncedAdvancedFilterState?.all_period) && canEnableAllPeriod(filters);
  const windowDays = allPeriodActive ? 0 : 30;
  const advancedFromState = filterStateToAdvancedParams(
    allPeriodActive
      ? debouncedAdvancedFilterState
      : { ...(debouncedAdvancedFilterState || {}), all_period: false },
    {
      tipo_scope: isSalesPipelineSelected ? "todos" : "relacionamento",
      default_date_window_days: windowDays,
    }
  );

  return {
    ...(advancedFromState || {
      tipo_scope: isSalesPipelineSelected ? "todos" : "relacionamento",
      default_date_window_days: windowDays,
    }),
    ...(tipoResponsavel && !advancedFromState?.tipo_responsavel
      ? { tipo_responsavel: tipoResponsavel }
      : {}),
  };
}

const QUEM_FILTERS = [
  "Minhas Solicitações",
  "Outros Usuários",
  "Fila",
  "ChatBots",
  "Agentes de IA",
];

/**
 * Monta o payload de filtros do atendimento para contagem na barra lateral.
 * Usa os filtros persistidos (mesmo foco da listagem do chat).
 */
export function buildAtendimentoCountRequest() {
  const filters = loadChatListFilters();
  const advancedState = loadChatAdvancedFilterState();
  const searchTerm = resolveChatListApiSearchTerm(loadChatListSearch());
  const filtersForApi = stripClientOnlyChatFilters(filters);

  const selectedQuem = filtersForApi.filter((f) => QUEM_FILTERS.includes(f));
  const multipleQuem = selectedQuem.length > 1;
  const nonQuemFilters = filtersForApi.filter((f) => !QUEM_FILTERS.includes(f));

  const advanced = resolveChatListAdvancedParams(filters, advancedState);

  let apiFilters = filtersForApi;
  let ownershipOrGroups = null;

  if (multipleQuem) {
    apiFilters = nonQuemFilters;
    ownershipOrGroups = selectedQuem.map((quem) => [...nonQuemFilters, quem]);
  }

  return {
    filters: apiFilters,
    searchTerm,
    advanced,
    ownershipOrGroups,
  };
}

/**
 * Payload das facetas do menu de filtros.
 * Envia os chips atuais para cruzar Quem × Status (mesmo recorte da lista).
 * Pipeline vira tipo_scope; não manda tipo_responsavel dos chips (entra no CASE).
 */
export function buildConversationFilterCountsRequest(filters, advancedState) {
  const list = Array.isArray(filters) ? filters : [];
  const isSalesPipelineSelected = list.includes("Pipeline de Vendas");
  const tipoScope = isSalesPipelineSelected ? "todos" : "relacionamento";
  const hasFila = list.includes("Fila");
  const defaultDateWindowDays = hasFila ? 14 : 30;
  const normalizedAdvanced = advancedState || {};
  const allPeriodActive =
    Boolean(normalizedAdvanced.all_period) && canEnableAllPeriod(list);
  const windowDays = allPeriodActive ? 0 : defaultDateWindowDays;
  const fromState = filterStateToAdvancedParams(
    allPeriodActive ? normalizedAdvanced : { ...normalizedAdvanced, all_period: false },
    {
      tipo_scope: tipoScope,
      default_date_window_days: windowDays,
    }
  );
  const advanced = {
    ...(fromState || {
      tipo_scope: tipoScope,
      default_date_window_days: windowDays,
    }),
    tipo_scope: tipoScope,
    default_date_window_days: windowDays,
  };
  return {
    filters: stripClientOnlyChatFilters(list).filter((f) => f !== "WhatsApp pessoal"),
    advanced,
  };
}

export function isQueueEligibleConversation(conversation) {
  if (!conversation?.id) return false;
  if (conversation.kind === "personal") return false;
  if (String(conversation.id).startsWith("wa-personal:")) return false;
  return true;
}
